import React, {PureComponent, Fragment} from 'react';
import styled from 'styled-components';
import {Link} from 'react-router-dom';
import {NotificationManager} from 'react-notifications';
import {Grid, Row, Col as UnstyledCol} from 'react-flexbox-grid';

import * as api from '../../lib/api';
import {cloneForm, canImportToForm} from '../../lib/form-helpers';
import withUrlState from '../../lib/with-url-state';
import {withUser} from '../../lib/user';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import {Tag, AddTagButton, TagsContainer} from '../../components/TagComponents';
import Button from '../../components/Button';
import Input from '../../components/Input';
import ListItem from '../../components/ListItem';
import ListScreen from '../../components/ListScreen';
import ExportFormDataModal from '../../components/ExportFormDataModal';
import Menu from '../../components/Menu';
import Icon from '../../components/Icon';
import Screen from '../../components/Screen';
import Content from '../../components/Content';
import SubHeader from '../../components/SubHeader';
import { T } from '../../components/I18n';

import TemplateLibraryModal from './TemplateLibraryModal';
import TemplateLibraryCard from './TemplateLibraryCard';
import DataImportModal from './DataImportModal';
import ImportStatusModal from './ImportStatusModal';
import TagManagementModal from '../../components/TagManagementModal';
import CanCreateTemplate from '../../security/rules/CanCreateTemplate';
import HasManagementRole from '../../security/rules/HasManagementRole';
import search from '../../lib/search';
import {formSort} from '../../lib/sort';
import TimeAgo from '../../components/TimeAgo';
import * as tagHelpers from '../../lib/tag-helpers';
import {groupTemplatesByCategory} from '../../lib/template-library';

class FormListScreen extends PureComponent {

  state = {
    loading: true,
    error: false,
    forms: [],
    universalTemplates: [],
    libraryOpen: false,
    exportForm: null,
    importForm: null,
    intervalId: null,
    dateFormats: [],
    failedImport: {intervalId: null, status: null, form: null},
    tagManagementForm: null,
    tagUpdateCounter: 0,
    availableTags: []
  };

  async componentDidMount() {
    const {credentials, user, match: {params: {importId}}, history} = this.props;

    try {
      const [forms, dateFormats, universalTemplates] = await Promise.all([
        api.getCompanyTemplates(credentials),
        user.role === "ADMIN" || user.role === "MANAGER" ? api.getSupportedDateFormats(credentials) : [],
        api.getLibraryTemplates(null, credentials).catch(() => [])
      ]);
      
      let availableTags = [];
      try {
        availableTags = await tagHelpers.fetchAvailableTags(credentials, 'form');
      } catch (error) {
        availableTags = tagHelpers.extractTagsFromItems(forms);
      }
      const intervalId = setInterval(this.pollChanges, 30000);
      const FailedImportIntervalId = setInterval(this.pollFailedImport, 1000);
      if (importId) {
        try {
          const expectedStatus = await api.getImportStatus(importId, credentials);
          let expectedForm = this.state.failedImport.form;
          
          for (let i in forms) {
            let form = forms[i];
            if (form.pendingImports.length === 0) {
              continue;
            }
            
            const importArray = form.pendingImports.filter(status => status.id === importId);
  
            if (importArray.length > 0) {
              expectedForm = form;
              break;
            }
          }
          this.setState({forms, universalTemplates, intervalId, dateFormats, failedImport: {status: expectedStatus, form: expectedForm, intervalId: FailedImportIntervalId}});
        }
        catch(e) {
          history.push(`/forms`);
          NotificationManager.error("This import has been canceled");
          this.setState({forms, universalTemplates, intervalId, dateFormats, availableTags, failedImport: {status: this.state.failedImport.status, form: this.state.failedImport.form, intervalId: FailedImportIntervalId}});
        }
      }
      else {
        this.setState({forms, universalTemplates, intervalId, dateFormats, availableTags, failedImport: {status: this.state.failedImport.status, form: this.state.failedImport.form, intervalId: FailedImportIntervalId}});
      }
    }
    catch(e) {
      this.setState({error: true});
    }
    finally {
      this.setState({loading: false});
    }
  }

  componentWillUnmount() {
    clearInterval(this.state.intervalId);
    clearInterval(this.state.failedImport.intervalId);
  }

  pollChanges = async () => {
    const {credentials, match: {params: {importId}}} = this.props;
    const {forms} = this.state;

    if(importId){
      const updatedForms = await Promise.all(forms.map(async form => {
        const updatedStatus = await Promise.all(form.pendingImports.map(async pending => {
          if (pending.state !== "COMPLETED_WITH_ERRORS" || pending.state !== "SUCCESS") {
            return await api.getImportStatus(pending.id, credentials);
          }
          return pending;
        }));
  
        const pendingImports = updatedStatus.filter(status => status.state !== "SUCCESS")
  
        return {...form, pendingImports};
      }));
  
      this.setState({forms: updatedForms});
    }
  }
  
  pollFailedImport = async () => {
    const {failedImport, forms} = this.state;
    const {credentials} = this.props;
    if (failedImport.status && (failedImport.status.state !== "COMPLETED_WITH_ERRORS" && failedImport.status.state !== "SUCCESS")) {
      let status = failedImport.status;
      const form = failedImport.form;
      const otherForms = forms.filter(f => f.id !== form.id)
      const restImports = form.pendingImports.filter(i => i.id !== status.id);
      let allForms = forms;
      try {
        status = await api.getImportStatus(failedImport.status.id, credentials);
        const thisForm = {...form, pendingImports: [...restImports, status].filter(s => s.state !== "SUCCESS")};
        allForms = [...otherForms, thisForm];
      }
      catch (e) {}
      this.setState({forms: allForms, failedImport: {intervalId: failedImport.intervalId, form, status}});
    }
  }

  handleFilterTextChange = e => {
    this.props.setUrlState({filter: e.target.value});
  }

  handleFormTypeChange = e => {
    this.props.setUrlState({type: e.target.value});
  }

  handleFormSortChange = e => {
    this.props.setUrlState({sort: e.target.value});
  }

  handleTagFilterChange = e => {
    this.props.setUrlState({tag: e.target.value});
  }

  handleOpenExport = form => {
    this.setState({exportForm: form});
  }

  handleCloseExport = () => {
    this.setState({exportForm: null});
  }

  handleOpenImportStatus = (status, form) => {
    this.setState({failedImport: {intervalId: this.state.failedImport.intervalId, status, form}});
  }

  handleCloseImportStatus = () => {
    const {history, match: {params: {importId}}} = this.props;
    if (importId) {
      history.push(`/forms`);
    }
    this.setState({failedImport: {intervalId: this.state.failedImport.intervalId, status: null, form: null}});
  }

  handleSuccusfulImportCompletion = (status, form) => {
    const {history, match: {params: {importId}}} = this.props;
    const {forms} = this.state;

    //Form can be null if you arrive here when status.state === "SUCCESS". The form will have no pending imports.
    let updatedForms = forms;
    if (form) {
      const otherForms = forms.filter(f => f.id !== form.id);
      const restImports = form.pendingImports.filter(i => i.id !== status.id);
      const thisForm = {...form, pendingImports: restImports};
      updatedForms = [...otherForms, thisForm];
    }

    this.setState({
      forms: updatedForms, 
      failedImport: {intervalId: this.state.failedImport.intervalId, status: null, form: null}
    }, () => {
      if (importId) {
        history.push(`/forms`);
      }
    });
  }

  updateImportStatus = (status, form) => {
    const {forms} = this.state;
    const otherForms = forms.filter(f => f.id !== form.id)
    const restImports = form.pendingImports.filter(i => i.id !== status.id);
    const thisForm = {...form, pendingImports: [...restImports, status]};

    this.setState({
      forms: [...otherForms, thisForm], 
      failedImport: {intervalId: this.state.failedImport.intervalId, status, form}
    });
  }

  handleClone = async form => {
    const {credentials} = this.props;

    //"Create a Copy" makes a plain form. library membership and its code belong to the
    //template that was copied, so they are not carried into the copy
    const {library, templateCode, description, category, ...source} = form;
    const newForm = cloneForm(`Copy of ${form.name}`, source);

    const newTab = window.open('', '_blank');

    try {
      const response = await api.createTemplate(newForm, credentials);

      if (newTab) {
        newTab.location.href = `/forms/${response.id}`;
      }

      this.setState(prevState => ({
        forms: [...prevState.forms, response]
      }));

      NotificationManager.success(`A copy of "${form.name}" was created`);
    }
    catch(e) {
      if (newTab) newTab.close();
      NotificationManager.error('Unable to copy form. Please try again');
    }
  }

  handlePublish = async form => {
    const {credentials} = this.props;

    // eslint-disable-next-line
    if(!confirm('Are you sure you want to publish this form? Published forms cannot be edited.')) {
      return;
    }

    try {
      await api.publishTemplate(form.id, credentials);
      NotificationManager.success(`Your "${form.name}" form was published`);

      this.setState(prevState => ({
        forms: prevState.forms.filter(f => f.id !== form.id).concat([{...form, published: true}])
      }));
    }
    catch(e) {
      NotificationManager.error('Unable to publish form. Please try again');
    }
  }

  handleArchive = async form => {
    const {credentials} = this.props;

    if(!window.confirm('Are you sure you want to archive this form?\nClipboard users can not interact with archived forms.')) {
      return;
    }

    try {
      await api.archiveTemplate(form.id, credentials);
      NotificationManager.success(`Your "${form.name}" form was archived`);
      
      this.setState(prevState => ({
        forms: prevState.forms.filter(f => f.id !== form.id).concat([{...form, current: false}])
      }));
    }
    catch(e) {
      NotificationManager.error('Unable to archive form. Please try again');
    }
  }

  handleUnarchive = async form => {
    const {credentials} = this.props;

    try {
      await api.unarchiveTemplate(form.id, credentials);
      NotificationManager.success(`Your "${form.name}" form was unarchived`);
      
      this.setState(prevState => ({
        forms: prevState.forms.filter(f => f.id !== form.id).concat([{...form, current: true}])
      }));
    }
    catch(e) {
      NotificationManager.error('Unable to archive form. Please try again');
    }
  }

  handleImport = importForm => {
    this.setState({importForm});
  }

  toggleImport = () => {
    this.setState({importForm: null});
  }

  toggleLibrary = () => {
    this.setState(prevState => ({
      libraryOpen: !prevState.libraryOpen,
      menuOpen: false
    }));
  }

  toggleMenu = () => {
    this.setState(prevState => ({
      menuOpen: !prevState.menuOpen
    }));
  }

  importScheduled = (form, importStatus) => {
    const forms = this.state.forms.filter(f => f.id !== form.id);
    this.setState({forms: [...forms, form], importForm: null}, () => this.handleOpenImportStatus(importStatus, form));
  }

  handleOpenTagManagement = form => {
    this.setState({tagManagementForm: form});
  }

  handleCloseTagManagement = () => {
    this.setState({tagManagementForm: null});
  }

  handleSaveTags = async (form, selectedTags, allAvailableTags, tagsToDelete = []) => {
    const {forms} = this.state;
    const {credentials} = this.props;
    
    const currentTagNames = this.state.availableTags.map(tagHelpers.getTagName);
    const result = await tagHelpers.saveTags(form, selectedTags, allAvailableTags, tagsToDelete, credentials, 'form', currentTagNames);
    
    if (result.success) {
      const updatedForms = tagHelpers.updateItemsAfterTagChanges(forms, form, selectedTags, tagsToDelete);
      const newAvailableTags = await tagHelpers.fetchAvailableTags(credentials, 'form');
      this.setState({
        forms: updatedForms,
        availableTags: newAvailableTags,
        tagManagementForm: null,
        tagUpdateCounter: this.state.tagUpdateCounter + 1
      });
    }
  }

  renderPrimaryAction = ({isSmallScreen}) => {
    const {user} = this.props;
    const {menuOpen} = this.state;
    if (!CanCreateTemplate(user)) {
      return null;
    }

    if (isSmallScreen) {
      return <Button to="/forms/create">Create form from blank</Button>;
    }

    return (
      <>
        <Button id="new-form" onClick={this.toggleMenu}>New Form <Icon name="chevron-down" /></Button>
        <Menu open={menuOpen} position="bottom-end" relativeTo="new-form" onClose={this.toggleMenu}>
          <Menu.Option to="/forms/create">Create from blank</Menu.Option>
          <Menu.Option onClick={this.toggleLibrary}>Create from a template</Menu.Option>
        </Menu>
      </>
    );
  }

  render() {
    const {user, urlState: {sort, type, filter, tag}} = this.props;
    const {loading, error, forms, universalTemplates, libraryOpen, exportForm, importForm, dateFormats, failedImport, tagManagementForm, tagUpdateCounter} = this.state;

    if (forms.length < 1 && !loading && !error) {
      return (
        <Screen title="Forms" activeLink="Forms" actions={[<Button to="/forms/create">New Form</Button>]}>
          <GetStarted>
            <Content>
              <FormExamples>
                <Instructions>
                  Select <Link to="/forms/create">New Form</Link> or choose a template below to begin
                </Instructions>
                {groupTemplatesByCategory(universalTemplates).map(group => (
                  <Fragment key={group.category}>
                    <SubHeaderContainer>
                      <SubHeader>{group.category}</SubHeader>
                    </SubHeaderContainer>
                    <Grid key={group.category} fluid>
                      <Row>
                        {group.templates.map(template => (
                          <Col key={template.id} sm={12} md={6} lg={4} xl={4}>
                            <TemplateLibraryCard template={template} />
                          </Col>
                        ))}
                      </Row>
                    </Grid>
                  </Fragment>
                ))}
              </FormExamples>
            </Content>
          </GetStarted>
        </Screen>
      );
    }

    const filteredAndSortedForms = forms
      .filter(filterFormsByType(type))
      .filter(form => search(form.name, filter))
      .filter(tagHelpers.createTagFilter(tag))
      .sort(formSort(sort, form => form.name, form => form.dates));
    
    const listKey = `${type}-${tag || 'all'}-${sort}-${tagUpdateCounter}`;

    return (
      <>
        <ListScreen
          key={listKey}
          title="Forms"
          activeLink="Forms"
          loading={loading && <Loading fullScreen />}
          error={error && <Error fullScreen />}
          items={filteredAndSortedForms}
          empty={<>There are no forms to display.{CanCreateTemplate(user) && <> Get started by <Link to="/forms/create">creating a new form.</Link></>}</>}
          search={filter}
          onSearch={this.handleFilterTextChange}
          primaryAction={this.renderPrimaryAction}
          mobileActions={[
            <Button onClick={this.toggleLibrary}>Create form from template</Button>
          ]}
          toolbarActions={[
            <Input id="form-sort" label="Sort by:" type="select" value={sort} onChange={this.handleFormSortChange}>
              <option value="asc">Alphabetical</option>
              <option value="desc">Reverse Alphabetical</option>
              <option value="createdDateNewest"> Created Date (newest first)</option>
              <option value="createdDateOldest"> Created Date (oldest first)</option>
              <option value="lastModifiedDateNewest"> Last Modified Date (newest first)</option>
              <option value="lastModifiedDateOldest"> Last Modified Date (oldest first)</option>
              <option value="lastSubmittedNewest"> Last Submitted (newest first)</option>
              <option value="lastSubmittedOldest"> Last Submitted (oldest first)</option>
            </Input>,
            <Input id="tag-filter" label="Tag:" type="select" value={tag || 'all'} onChange={this.handleTagFilterChange} style={{maxWidth: '200px', minWidth: '120px'}}>
              <option value="all">All Tags</option>
              {this.state.availableTags.map(tagObj => {
                const tagName = typeof tagObj === 'string' ? tagObj : tagObj.name;
                return (
                  <option key={tagName} value={tagName} title={tagName}>
                    {tagName.length > 30 ? tagName.substring(0, 30) + '...' : tagName}
                  </option>
                );
              })}
            </Input>,
            CanCreateTemplate(user) && (
              <Input id="form-type-filter" label="Filter:" type="select" value={type} onChange={this.handleFormTypeChange}>
                <option value="published">Published Forms</option>
                <option value="unpublished">Unpublished Forms</option>
                <option value="archived">Archived Forms</option>
                <option value="all">All Forms</option>
              </Input>
            )
          ]}>
          {(form, i) => (
            <ListItem
              key={`${form.id}-${form.tags ? form.tags.length : 0}-${JSON.stringify(form.tags)}`}
              first={i === 0}
              notice={form.pendingImports.length > 0 && <ImportOptions form={form} onSelect={this.handleOpenImportStatus} />}
              options={[
                {title: 'View Data', to: `/sheet/${form.id}/all`},
                CanCreateTemplate(user) && {title: 'Edit', to: `/forms/${form.id}`},
                !form.published && form.current && {title: 'Publish', onClick: () => this.handlePublish(form)},
                CanCreateTemplate(user) && form.current && {title: 'Archive', onClick: () => this.handleArchive(form)},
                CanCreateTemplate(user) && !form.current && {title: 'Unarchive', onClick: () => this.handleUnarchive(form)},
                CanCreateTemplate(user) && {title: 'Create a Copy', onClick: () => this.handleClone(form)},
                {title: 'Modify Tags', onClick: () => this.handleOpenTagManagement(form)},
                HasManagementRole(user) && canImportToForm(form) && {title: 'Import Data', onClick: () => this.handleImport(form)},
                user.role !== "USER" && {title: 'Export Data', onClick: () => this.handleOpenExport(form)}
              ].filter(Boolean)}
            >
              <FormItemContent>
                <strong>{user.role === 'ADMIN' ? <Link to={`/forms/${form.id}`}>{form.name}</Link> : form.name}</strong>
                <FormSubtitle>
                  <span>Last Submitted: {form.dates.lastSubmitted ? <TimeAgo date={form.dates.lastSubmitted}/> : 'Never'}</span>
                  <span>Last Modified: <TimeAgo date={form.dates.lastModified}/></span>
                  <TagsContainer variant="form" hasContent={!!(form.tags && form.tags.length > 0)}>
                    {form.tags && form.tags.length > 0 && form.tags.map(tag => {
                      const tagName = typeof tag === 'string' ? tag : tag.name;
                      return (
                        <Tag key={tagName} onClick={() => this.handleTagFilterChange({target: {value: tagName}})}>{tagName}</Tag>
                      );
                    })}
                    {form.tags && form.tags.length > 0 && (
                      <AddTagButton onClick={() => this.handleOpenTagManagement(form)}>
                        <Icon name="plus" size="1em" />
                      </AddTagButton>
                    )}
                  </TagsContainer>
                </FormSubtitle>
              </FormItemContent>
            </ListItem>
          )}
        </ListScreen>
        <ExportFormDataModal
          form={exportForm}
          onClose={this.handleCloseExport}
        />
        <TemplateLibraryModal
          open={libraryOpen}
          onClose={this.toggleLibrary}
        />
        <DataImportModal 
          form={importForm}
          dateFormats={dateFormats}
          onClose={this.toggleImport}
          onImportScheduled={this.importScheduled}
        />
        <ImportStatusModal 
          status={failedImport.status}
          form={failedImport.form}
          onClose={this.handleCloseImportStatus}
          onImportSuccess={this.handleSuccusfulImportCompletion}
          updateImportStatus={this.updateImportStatus}
        />
        <TagManagementModal
          open={!!tagManagementForm}
          onClose={this.handleCloseTagManagement}
          item={tagHelpers.normalizeItemForEditing(tagManagementForm)}
          itemName={tagManagementForm && tagManagementForm.name}
          availableTags={this.state.availableTags}
          entityType="form"
          onSave={this.handleSaveTags}
        />
      </>
    );
  }
}

function filterFormsByType(type) {
  return form => {
    switch (type) {
      case 'published':
        return form.published && form.current;
      case 'unpublished':
        return !form.published && form.current;
      case 'archived':
        return !form.current;
      default:
        return true;
    }
  };
}

class ImportOptions extends PureComponent {
  state = {
    menuOpen: false
  }

  openMenu = () => {
    this.setState({menuOpen: true});
  }

  closeMenu = () =>  {
    this.setState({menuOpen: false});
  }

  render() {
    const {form, onSelect} = this.props;
    const {menuOpen} = this.state;
    return(
      <>
        <Button id={`pending-import-${form.id}`} onClick={this.openMenu} tertiary>
          <T isolate><T variable="pendingImportsLength">{form.pendingImports.length}</T> pending imports</T>
        </Button>
        <Menu open={menuOpen} position="bottom-end" relativeTo={`pending-import-${form.id}`} onClose={this.closeMenu}>
          {form.pendingImports.map((status, i) => 
            <Menu.Option key={i} onClick={() => onSelect(status, form)}>
              <Container>
                <T isolate><LeftColumn><T variable="fileName">{status.fileName}</T></LeftColumn></T>
                <RightColumn>{(status.state === "COMPLETED_WITH_ERRORS" || status.state === "PROCESSING_WITH_ERRORS") && <Icon color="#FFB300" name="alert-triangle"/>}</RightColumn>
              </Container>
            </Menu.Option>
          )}
        </Menu>
      </>
    )
  }
}

const GetStarted = styled.div`
  background-image: url('/assets/construction.png'), url('/assets/city-landscape.png');
  background-attachment: fixed, fixed;
  background-position: center bottom, center bottom;
  background-repeat: no-repeat, repeat-x;
  min-height: calc(100vh - 7.625rem);
`;

const FormExamples = styled.div`
  padding-bottom: 28rem;
  margin: 0 auto;
  max-width: 75rem;
`;

const Col = styled(UnstyledCol)`
  padding: 0.9375rem !important;
`;

const Instructions = styled.p`
  text-align: center;
  margin: 0;
  font-size: 1.25rem;
  padding: 4rem 1rem;
  line-height: 1.5;

  a {
    border-bottom: 1px solid #0049ee;
  }
`;

const SubHeaderContainer = styled.div`
  background: rgba(245, 245, 248, 0.7);
  padding: 2rem 0 1rem 0;
  margin: -0.9375rem 0 -0.9375rem 0;
`;

const defaultUrlState = {
  sort: 'asc',
  type: 'published', 
  filter: '',
  tag: 'all'
};

const Container = styled.div`
  display: flex;
  max-width: 100%;
  align-items: center;
  border-top: none;
`;

const LeftColumn = styled.div`
  flex: 1;
  line-height: 1.50;
  min-width: 0;
`;

const RightColumn = styled.div`
  padding-left: 1rem;
`;

const FormItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  
  strong {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const FormSubtitle = styled.small`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  color: #666;
  
  span {
    display: block;
  }
`;


export default withUser(withUrlState(defaultUrlState)(FormListScreen));
